/*
 * NOTE: This should be kept in sync with back-end permissions.yml. Permission
 *       scopes are written to by the frontend permission config checkboxes but
 *       are not defined by the frontend.
 *
 *       If you have any questions, just ask James or check go/permissions
 */
export enum Permission {
  AccountActionCreate = 'account-action.create',
  AccountActionView = 'account-action.view',
  AccountActionWrite = 'account-action.write',
  AccountCreate = 'account.create',
  AccountIdNumbersView = 'account.government-id.identification-number.view',
  AccountList = 'account.list',
  AccountRedact = 'account.redact',
  AccountsExport = 'accounts.export',
  AccountSsnView = 'account.ssn.view',
  AccountTypeCreate = 'account-type.create',
  AccountTypeView = 'account-type.view',
  AccountTypeWrite = 'account-type.write',
  AccountView = 'account.view',
  AccountWrite = 'account.write',
  ApiKeyView = 'api-key.view',
  ApiKeyWrite = 'api-key.write',
  ApiLogList = 'api-log.list',
  ApiLogsView = 'api-logs.view',
  BlueprintList = 'blueprint.list',
  BlueprintWrite = 'blueprint.write',
  CaseActionCreate = 'case-action.create',
  CaseActionView = 'case-action.view',
  CaseActionWrite = 'case-action.write',
  CaseAllList = 'case.all.list',
  CaseAllView = 'case.all.view',
  CaseAnalyticsView = 'case-analytics.view',
  CaseAssign = 'case.assign',
  CaseAssignmentPolicyView = 'case-assignment-policy.view',
  CaseAssignmentPolicyWrite = 'case-assignment-policy.write',
  CaseBulkAssign = 'case.bulk.assign',
  CaseBulkWrite = 'case.bulk.write',
  CaseCreate = 'case.create',
  CaseGovIdNumberView = 'case.government-id.identification-number.view',
  CaseList = 'case.list',
  CaseRedact = 'case.redact',
  CasesExport = 'cases.export',
  CaseTemplateCreate = 'case-template.create',
  CaseTemplateView = 'case-template.view',
  CaseTemplateWrite = 'case-template.write',
  CaseQueueView = 'case-queue.view',
  CaseQueueWrite = 'case-queue.write',
  CaseView = 'case.view',
  CaseWrite = 'case.write',
  DashboardVerificationsNormalizedView = 'dashboard.verifications.normalized.view',
  EnvironmentList = 'environment.list',
  EnvironmentView = 'environment.view',
  EnvironmentWrite = 'environment.write',
  ExternalIntegrationLogList = 'external-integration-log.list',
  ExternalIntegrationLogView = 'external-integration-log.view',
  ExternalIntegrationLogWrite = 'external-integration-log.write',
  ExternalIntegrationCreate = 'external-integration.create',
  ExternalIntegrationView = 'external-integration.view',
  ExternalIntegrationWrite = 'external-integration.write',
  GraphQueryTemplateCreate = 'graph.query-template.create',
  GraphQueryTemplateView = 'graph.query-template.view',
  GraphQueryTemplateWrite = 'graph.query-template.write',
  GraphQueryView = 'graph.query.view',
  GraphQueryWrite = 'graph.query.write',
  GraphsExport = 'graphs.export',
  GraphView = 'graph.view',
  GraphWrite = 'graph.write',
  ImporterPipelineView = 'importer-pipeline.view',
  ImporterPipelineWrite = 'importer-pipeline.write',
  InquiriesExport = 'inquiries.export',
  InquiryAnalyticsView = 'inquiry-analytics.view',
  InquiryCreate = 'inquiry.create',
  // InquiryDecision should be mutually exclusive to InquiryWrite
  // TODO: (eileen) Add permission to ProductsSectionData
  InquiryDecision = 'inquiry.decision',
  InquiryGovIdNumberView = 'inquiry.government-id.identification-number.view',
  InquiryList = 'inquiry.list',
  InquiryRedact = 'inquiry.redact',
  // InquiryResume should be mutually exclusive to InquiryWrite
  // TODO: (eileen) Add permission to ProductsSectionData
  InquiryResume = 'inquiry.resume',
  InquirySsnView = 'inquiry.ssn.view',
  InquiryTemplateCreate = 'inquiry-template.create',
  InquiryTemplateDocumentExtractionConfigEdit = 'inquiry-template.document.extraction-config-editor.edit',
  InquiryTemplateDocumentExtractionConfigView = 'inquiry-template.document.extraction-config-editor.view',
  InquiryTemplateView = 'inquiry-template.view',
  InquiryTemplateWrite = 'inquiry-template.write',
  InquiryView = 'inquiry.view',
  InquiryWrite = 'inquiry.write',
  InvoiceList = 'invoice.list',
  InvoiceView = 'invoice.view',
  ListCreate = 'list.create',
  ListItemView = 'list-item.view',
  ListItemWrite = 'list-item.write',
  ListView = 'list.view',
  ListWrite = 'list.write',
  OrganizationView = 'organization.view',
  OrganizationWrite = 'organization.write',
  ReportAllView = 'report.all.view',
  ReportCreate = 'report.create',
  ReportExport = 'report.export',
  ReportIgnoreHitsWrite = 'report.ignore-hits.write',
  ReportList = 'report.list',
  ReportPauseWrite = 'report.pause.write',
  ReportRedact = 'report.redact',
  ReportSsnView = 'report.ssn.view',
  ReportTemplateCreate = 'report-template.create',
  ReportTemplateView = 'report-template.view',
  ReportTemplateWrite = 'report-template.write',
  ReportView = 'report.view',
  ReportWrite = 'report.write',
  RoleAssignUser = 'role.assign-user',
  RoleView = 'role.view',
  RoleWrite = 'role.write',
  SARConfigurationView = 'sar-configuration.view',
  SARConfigurationWrite = 'sar-configuration.write',
  SARCreate = 'sar.create',
  SARExport = 'sar.export',
  SARFile = 'sar.file',
  SARList = 'sar.list',
  SARView = 'sar.view',
  SARWrite = 'sar.write',
  TableViewView = 'table-view.view',
  TableViewWrite = 'table-view.write',
  TeamView = 'team.view',
  TeamWrite = 'team.write',
  TransactionCreate = 'transaction.create',
  TransactionList = 'transaction.list',
  TransactionRedact = 'transaction.redact',
  TransactionsExport = 'transactions.export',
  TransactionTypeCreate = 'transaction-type.create',
  TransactionTypeView = 'transaction-type.view',
  TransactionTypeWrite = 'transaction-type.write',
  TransactionView = 'transaction.view',
  TransactionWrite = 'transaction.write',
  UserAuditLogsAllUsersView = 'user-audit-logs.all-users.view',
  UserAvailabilityAllUsersWrite = 'user.availability.all-users.write',
  UserMfaAllUsersView = 'user.mfa.all-users.view',
  UserSessionsAllUsersView = 'user-sessions.all-users.view',
  UserSessionsWrite = 'user-sessions.write',
  UserView = 'user.view',
  UserWrite = 'user.write',
  VerificationDatabaseSsnView = 'verification.database.ssn.view',
  VerificationDocumentClassificationsAiTagView = 'verification.document.classifications.ai-tag.view',
  VerificationGovIdNumberView = 'verification.government-id.identification-number.view',
  VerificationList = 'verification.list',
  VerificationRedact = 'verification.redact',
  VerificationTemplateCreate = 'verification-template.create',
  VerificationTemplateView = 'verification-template.view',
  VerificationTemplateWrite = 'verification-template.write',
  VerificationView = 'verification.view',
  VerificationWrite = 'verification.write',
  WebhookEventView = 'webhook-event.view',
  WebhookEventWrite = 'webhook-event.write',
  WebhookView = 'webhook.view',
  WebhookWrite = 'webhook.write',
  WorkflowCreate = 'workflow.create',
  WorkflowRunList = 'workflow-run.list',
  WorkflowVersionList = 'workflow-version.list',
  WorkflowView = 'workflow.view',
  WorkflowWrite = 'workflow.write',

  // Cases Team
  RobinhoodPiiView = 'robinhood.pii.view',
}
